import { ReactElement, memo, useState } from 'react';
import * as S from './CreateYourBooking.styles';
import { Schedule, Ticket } from '@/common/service/api/Availability/Availability.domain';
import { TSelected, TStatus } from '@/common/app/contexts/ActivityContext';
import { TicketLabel } from '../TicketLabel/TicketLabel';
import { TicketsCounter } from '@/common/ui/inputs/TicketsCounter/TicketsCounter';

type CreateYourBookingProps = {
    selectedSchedule?: Schedule;
    status: TStatus;
    tickets: Ticket[];
    selected: TSelected;
    needTicketsGuests: boolean;
    onClickTicket: (ticketId: number) => (index: number) => void;
    stepNumbers: number[];
    isGiftCard: boolean;
};

export const CreateYourBookingContainer = ({
    selectedSchedule,
    tickets,
    selected,
    needTicketsGuests,
    onClickTicket,
    stepNumbers,
    isGiftCard,
}: CreateYourBookingProps): ReactElement => {
    const [isTouched, setIsTouched] = useState(false);

    const title = isGiftCard ? ' Choose the Amount' : ' Create your Booking';
    const ticketsErrorText = isGiftCard
        ? 'Please choose the amount'
        : 'You need at least 1 ticket(s)';
    const isSelected = tickets.some(({ id }) => !!selected.tickets[id]);
    const errorMsg = isTouched && !isSelected && ticketsErrorText;

    if (!tickets.length || !selectedSchedule) {
        return <></>;
    }

    return (
        <S.WrapperCreateYourBooking>
            <S.Subtitle data-test-id="how-many-tickets" id="tickets">
                {stepNumbers[1]}.{title}
                {Number(selectedSchedule?.min_tickets) > 1 && (
                    <S.SubtitleNote id="min-tickets">{` minimum: ${selectedSchedule?.min_tickets}`}</S.SubtitleNote>
                )}
            </S.Subtitle>
            <S.OptionGrid>
                {tickets.map((ticket, index) => (
                    <TicketsCounter
                        key={ticket.id}
                        onSelect={onClickTicket(ticket.id)}
                        selected={selected.tickets[ticket.id]}
                        lable={
                            <TicketLabel
                                name={ticket.name}
                                description={ticket.description}
                                price={ticket.price}
                                strikeOut={ticket.strike_out}
                                avail={isGiftCard ? ticket.avail : undefined}
                            />
                        }
                        needTicketsGuests={needTicketsGuests}
                        ticketId={ticket.id}
                        onTouch={!isTouched ? () => setIsTouched(true) : undefined}
                        errorMsg={index === 0 && errorMsg}
                        closeErrorMsg={
                            index === 0 && errorMsg ? () => setIsTouched(false) : undefined
                        }
                        minCount={+ticket.min}
                        maxCount={ticket.avail}
                    />
                ))}
            </S.OptionGrid>
        </S.WrapperCreateYourBooking>
    );
};

export const CreateYourBooking = memo(CreateYourBookingContainer);
